import React, { useState } from "react";
import styled from "styled-components";
import { ExternalLink } from "react-feather";
import { Button, Input } from "@cinesys/ui/components";
/**
 * DEV-ONLY launcher home (mounted at the index route only when
 * `NODE_ENV !== "production"`; a deployed build redirects `/` straight to `/home` instead — see
 * AppRouter's doc comment and `docs/decisions.md`. Nested under the same `ShellLayout` as the real
 * page (gets `ShellHeader` above it too, harmless for a dev-only screen). This component must
 * never affect that prod path.
 *
 * Production is launched BY iconik with `?authorization_token=` in the URL,
 * captured once in `index.tsx` (before the app mounts) and exchanged for an
 * httpOnly session cookie (cookie-auth cutover, Monday 12656019094) — there is
 * no client-readable auth token anymore for this screen to reuse.
 *
 * COOKIE-AUTH CUTOVER: this used to read/write the token through
 * `CustomerApiService.getToken()`/`setToken()`, the SAME storage the real app
 * used (`localStorage`). That storage is gone (the real token now lives only
 * in an httpOnly cookie, invisible to JS), so this screen now keeps its own
 * separate, purely-cosmetic `TOKEN_KEY` — mirrors vault-frontend's `DevHome`,
 * which always needed its own `vault_dev_token` key for the same reason
 * (vault's `ApiService` never persisted its token at all). This key is ONLY
 * ever read by this dev-only screen to remember what you last pasted here; it
 * has no bearing on auth.
 *
 * The page links below are full-page `<a>` navigations (not router `<Link>`s)
 * with the token baked into `?authorization_token=`, so opening one boots a
 * fresh SPA instance and exercises the real `index.tsx` capture-and-exchange
 * path end to end — not a shortcut that only pre-seeds a cookie.
 */

const TOKEN_KEY = "customer_dev_token";
// Phase 2 baseline surfaces aren't tied to a token org the way ab-vault/spotter are, but
// item-detail needs a real asset id to fetch against — remembered the same way the token is, so
// this form doesn't forget it between reloads.
const ASSET_ID_KEY = "customer_dev_item_detail_asset_id";

const PAGES: { route: string; label: string }[] = [
  {
    route: "ab-vault/candidate-assignments",
    label: "AB Vault — Candidate Assignments",
  },
  { route: "spotter/fields", label: "Spotter — Fields" },
  { route: "spotter/gamebook", label: "Spotter — Gamebook" },
  { route: "spotter/rename", label: "Spotter — File Rename" },
  { route: "spotter/usage", label: "Spotter — Usage" },
  { route: "home", label: "Home Dashboard" },
  {
    // Anchor Review's own tab/route is gone (Monday 12871494046, consolidation round) — it's
    // reachable from a Films Ground/Top Films row on this same Control Room page now.
    route: "spotter/control-room",
    label: "Spotter — Control Room",
  },
  {
    route: "metadata-import",
    label: "Metadata Import & Enrichment (UI spike)",
  },
];

const Shell = styled.div`
  min-height: 100%;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 60px 24px;
`;

const Card = styled.div`
  background: var(--dark090);
  border: 1px solid var(--dark300);
  border-radius: 8px;
  padding: 28px;
  width: 100%;
  max-width: 680px;
`;

const Title = styled.h1`
  margin: 0 0 6px;
  font-size: 20px;
  color: var(--gray500);
`;

const Description = styled.p`
  margin: 0 0 24px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--dark700);

  code {
    background: var(--dark100);
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 12px;
  }
`;

const TokenRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 12px;
  margin-bottom: 28px;
`;

const Links = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PageLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 14px 16px;
  border-radius: 6px;
  background: var(--dark100);
  color: var(--gray500);
  text-decoration: none;
  border: 1px solid var(--dark300);
  transition: all 120ms;

  &:hover {
    border-color: var(--blue300);
    background: var(--dark110);
  }

  small {
    display: block;
    color: var(--dark700);
    font-size: 12px;
    margin-top: 2px;
  }
`;

const DevHome = () => {
  const [token, setToken] = useState<string>(
    () => localStorage.getItem(TOKEN_KEY) ?? ""
  );
  const [saved, setSaved] = useState(false);
  const [assetId, setAssetId] = useState<string>(
    () => localStorage.getItem(ASSET_ID_KEY) ?? ""
  );

  const save = (value: string) => {
    setToken(value);
    if (value) {
      localStorage.setItem(TOKEN_KEY, value);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setSaved(true);
  };

  const href = (route: string) =>
    token
      ? `/${route}?authorization_token=${encodeURIComponent(token)}`
      : `/${route}`;

  return (
    <Shell>
      <Card>
        <Title>Customer Portal — Dev Home</Title>
        <Description>
          DEV ONLY. Paste an iconik launch token below — it&apos;s saved to{" "}
          <code>localStorage[&quot;{TOKEN_KEY}&quot;]</code> (just so this form
          remembers it; the real app never reads this key) and baked into the
          links below. Opening a link boots a fresh SPA instance with the token
          in the URL, exactly like iconik does in production, and exchanges it
          for a session cookie the same way.
        </Description>

        <TokenRow>
          <Input
            label="iconik Auth-Token"
            placeholder="paste a token (stored in localStorage)"
            value={token}
            onChange={setToken}
            clearable
            onBlur={() => save(token)}
          />
          <Button
            label={saved ? "Saved" : "Save"}
            type="success"
            onClick={() => save(token)}
          />
        </TokenRow>

        <Links>
          {PAGES.map((p) => (
            <PageLink key={p.route} href={href(p.route)}>
              <span>
                {p.label}
                <small>/{p.route}</small>
              </span>
              <ExternalLink size={16} />
            </PageLink>
          ))}
        </Links>

        <TokenRow>
          <Input
            label="Item Detail — asset id"
            placeholder="paste a real iconik asset id"
            value={assetId}
            onChange={(value) => {
              setAssetId(value);
              if (value) {
                localStorage.setItem(ASSET_ID_KEY, value);
              } else {
                localStorage.removeItem(ASSET_ID_KEY);
              }
            }}
            clearable
          />
        </TokenRow>
        {assetId && (
          <Links>
            <PageLink href={href(`item-detail/${encodeURIComponent(assetId)}`)}>
              <span>
                Item Detail
                <small>/item-detail/{assetId}</small>
              </span>
              <ExternalLink size={16} />
            </PageLink>
          </Links>
        )}
      </Card>
    </Shell>
  );
};

export default DevHome;
